<script setup lang="ts">
import { ElIcon } from 'element-plus'

import { copyToClipboard } from '@/shared/lib'
import { CopyDocument } from '@/shared/icons'

/**
 * The small copy glyph next to a value — a token, an id, a link — that copies `text` and
 * toasts the outcome. A whole block the user is meant to take away is `CodeArea`, which
 * copies itself when clicked; a long value plus a preview is `CopyDialog`.
 *
 * Give it a `label` saying *what* is copied: that is the button's accessible name, and
 * "Copy" on its own tells a screen reader nothing about which of four values it means.
 */
const props = withDefaults(
  defineProps<{
    text?: string
    label?: string
    successText?: string
  }>(),
  { label: 'Copy', successText: 'Copied!' }
)

function copy() {
  if (!props.text) return
  copyToClipboard(props.text, { success: props.successText })
}
</script>

<template>
  <button type="button" class="copy-button" :aria-label="label" @click="copy">
    <ElIcon><CopyDocument /></ElIcon>
  </button>
</template>

<style lang="scss" scoped>
.copy-button {
  display: inline-flex;
  padding: 2px;
  color: var(--el-text-color-secondary);
  cursor: pointer;
  background: none;
  border: none;
}

.copy-button:hover {
  color: var(--el-color-primary);
}
</style>
